import { useEffect, useRef, useState } from 'react';
import type { SceneAssets, MobileBehavior } from './manifest';
import { safeImageUrl } from '../../renderer/url-safety';

function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 768px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isMobile;
}

interface SceneMediaProps {
  assets: SceneAssets;
  mobileBehavior: MobileBehavior;
  className?: string;
}

export function SceneMedia({ assets, mobileBehavior, className = '' }: SceneMediaProps) {
  const isMobile = useIsMobileViewport();
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [videoFailed, setVideoFailed] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isInViewport, setIsInViewport] = useState(false);

  const wantVideo =
    assets.renderStrategy === 'video_primary' &&
    assets.primaryVideo &&
    !(isMobile && mobileBehavior.disableVideo);

  const primaryImageUrl = safeImageUrl(assets.primaryImage.publicUrl);
  const primaryVideoUrl = wantVideo && !videoFailed ? safeImageUrl(assets.primaryVideo!.publicUrl) : null;

  // Viewport-aware video observer: pause video when offscreen to preserve GPU decoders & battery
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !primaryVideoUrl) return;

    // No IntersectionObserver (older browsers, jsdom): just play.
    if (typeof IntersectionObserver === 'undefined') {
      setIsInViewport(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInViewport(entry.isIntersecting);
      },
      {
        rootMargin: '250px 0px 250px 0px',
        threshold: 0.05,
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [primaryVideoUrl]);

  // Play/pause based on visibility to avoid concurrent 6-stream decoding bottleneck
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !primaryVideoUrl) return;

    if (isInViewport) {
      const playPromise = typeof video.play === 'function' ? video.play() : undefined;
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Ignore interruption/autoplay restriction gracefully
        });
      }
    } else {
      video.pause();
    }
  }, [isInViewport, primaryVideoUrl]);

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full overflow-hidden ${className}`}
      style={{ transform: 'translateZ(0)' }}
    >
      {/* Instant Sharp Image Base Layer — Renders immediately without layout lag */}
      {!imageFailed ? (
        <img
          src={primaryImageUrl}
          alt="ceremony scene"
          loading="eager"
          decoding="async"
          onError={() => setImageFailed(true)}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ transform: 'translateZ(0)', willChange: 'transform' }}
        />
      ) : (
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            background: `var(--scene-gradient, linear-gradient(135deg, rgba(200,150,100,0.1), rgba(100,150,200,0.1)))`,
          }}
        />
      )}

      {/* Hardware-Accelerated Video Layer — Cross-fades seamlessly over image */}
      {primaryVideoUrl && (
        <video
          ref={videoRef}
          key={`video-${primaryVideoUrl}`}
          muted
          loop
          playsInline
          preload="metadata"
          poster={primaryImageUrl}
          onCanPlayThrough={() => setIsVideoReady(true)}
          onLoadedData={() => setIsVideoReady(true)}
          onError={() => setVideoFailed(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            isVideoReady ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transform: 'translateZ(0)', willChange: 'transform' }}
        >
          <source src={primaryVideoUrl} type={assets.primaryVideo?.mimeType || 'video/mp4'} />
        </video>
      )}
    </div>
  );
}
